import { useCallback } from "react";

import type { IAppointmentTableEndpoint } from "@api/appointment";
import type { IAppointmentCardProps } from "@components/organisms/appointmentCard/types";
import type { IAppointmentCardTopProps } from "@components/organisms/appointmentCard/components/appointmentCardTop";
import { getFullName, useConvertDate } from "@utils";

export const useAppointmentConverter = () => {
  const { humanizeFullDate } = useConvertDate();

  const mapAppointmentToAppointmentInfo = useCallback(
    (data: IAppointmentTableEndpoint): IAppointmentCardTopProps => {
      return {
        appointmentInfo: {
          id: data?.appt_id,
          name: data?.facility?.value,
          details: { date: humanizeFullDate(data?.date) },
        },
        doctorInfo: {
          id: data?.doctor?.user_id,
          name: data?.doctor?.first_name,
          lastName: data?.doctor?.last_name,
          avatarUrl: data?.doctor?.avatar?.url,
          details: {
            degree: data?.doctor?.degree?.value,
            speciality: data?.doctor?.speciality?.value,
          },
        },
        patientInfo: {
          id: data?.patient?.user_id,
          name: data?.patient?.first_name,
          lastName: data?.patient?.last_name,
          avatarUrl: data?.patient?.avatar?.url,
          details: {
            age: data?.patient?.age,
            gender: data?.patient?.gender?.value,
          },
        },
      };
    },
    [humanizeFullDate],
  );

  const mapAppointmentToAppointmentList = useCallback(
    (data: IAppointmentTableEndpoint): IAppointmentCardProps => {
      const apptData = mapAppointmentToAppointmentInfo(data);

      return {
        id: data?.id,
        variant: "appointment",
        visitType: data.visit_type.key,
        status: data?.status,
        data: apptData,
        canCancel: data?.can_cancel,
        canJoin: data?.can_join,
        startDate: new Date(data?.date),
        canViewRemainingTime: data?.can_view_remaining_time,
        canDecline: data?.can_decline,
        canAccept: data?.can_accept,
        canViewDetail: data?.can_view_detail,
        canViewSummary: data?.can_view_summary,
        canWriteSummaryToAppointment: data?.can_write_summary_to_appointment,
        canWriteAddendum: data?.can_write_addendum,
        canWriteSummary: data?.can_write_summary,
        canWriteComment: data?.can_write_comment,
        canNoShow: data?.can_noShow,
        actions: {
          canViewStoredResult: data?.can_view,
          canReschedule: data?.can_reschedule,
          canEditIntakeForm: data?.can_edit,
          canViewIntakeForm: data?.can_view,
        },
        // TODO CHECK
        appointmentType: "all",
        summaryNoteId: data?.summary_note_id,
        doctorName: getFullName({
          firstName: data?.doctor?.first_name,
          lastName: data?.doctor?.last_name,
        }),
      };
    },
    [mapAppointmentToAppointmentInfo],
  );

  const mapAppointmentToIntakeFormList = useCallback(
    (data: IAppointmentTableEndpoint): IAppointmentCardProps => {
      const apptData = mapAppointmentToAppointmentInfo(data);

      return {
        id: data?.id,
        visitType: data.visit_type.key,
        variant: "intake-form",
        status: data?.status,
        data: apptData,
        canCancel: data?.can_cancel,
        actions: {
          canViewIntakeForm: data?.can_view,
          canReschedule: data?.can_reschedule,
          canEditIntakeForm: data?.can_edit,
        },
        // TODO CHECK
        appointmentType: "all",
        summaryNoteId: data?.summary_note_id,
        doctorName: getFullName({
          firstName: data?.doctor?.first_name,
          lastName: data?.doctor?.last_name,
        }),
      };
    },
    [mapAppointmentToAppointmentInfo],
  );

  const mapAppointmentToSummaryNoteManagementList = useCallback(
    (data: IAppointmentTableEndpoint): IAppointmentCardProps => {
      const apptData = mapAppointmentToAppointmentInfo(data);

      return {
        id: data?.id,
        visitType: data.visit_type.key,
        variant: "summary-note",
        data: apptData,
        canViewSummary: data?.can_view_summary,
        canWriteAddendum: data?.can_write_addendum,
        canWriteSummary: data?.can_write_summary,
        actions: {
          canPrint: false,
          canDownload: false,
        },
        // TODO CHECK
        appointmentType: "all",
        summaryNoteId: data?.summary_note_id,
        doctorName: getFullName({
          firstName: data?.doctor?.first_name,
          lastName: data?.doctor?.last_name,
        }),
      };
    },
    [mapAppointmentToAppointmentInfo],
  );

  const mapAppointmentToSummaryNoteList = useCallback(
    (data: IAppointmentTableEndpoint): IAppointmentCardProps => {
      const apptData = mapAppointmentToAppointmentInfo(data);

      return {
        id: data?.id,
        visitType: data.visit_type.key,
        variant: "past-appointment",
        data: apptData,
        actions: {
          canPrint: false,
          canDownload: false,
        },
        // TODO CHECK
        appointmentType: "all",
        summaryNoteId: data?.summary_note_id,
        doctorName: getFullName({
          firstName: data?.doctor?.first_name,
          lastName: data?.doctor?.last_name,
        }),
      };
    },
    [mapAppointmentToAppointmentInfo],
  );

  return {
    mapAppointmentToAppointmentList,
    mapAppointmentToSummaryNoteManagementList,
    mapAppointmentToSummaryNoteList,
    mapAppointmentToIntakeFormList,
  };
};
